import { Selector } from "globalComponents";
import React from "react";
import styled from "styled-components";
import { getTheme } from "Theme";
import { ReactComponent as Up } from "../../assets/upArrow.svg";
import { ReactComponent as Down } from "../../assets/downArrow.svg";
import { Loader } from "semantic-ui-react";
import { DefaultRow } from "./DefaultRow";
import { NotificationRow } from "./NotificationRow";

const theme = getTheme();

export const RowType = {
  default: "default",
  notification: "notification",
};

const Main = styled.table`
  display: flex;
  flex-direction: column;
  opacity: ${(props) => props.isLoading && 0.8};
  min-height: ${(props) => props.isLoading && "400px"};
  ${(props) =>
    props.isLoading &&
    `
   opacity: .8;
   min-height: 400px;
   pointer-events: none;
  `}
`;

const Header = styled.tr`
  display: flex;
  flex-direction: row;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: white;
  padding: 8px;
  border-bottom: 2px solid ${theme.BLACK};
  background: ${theme.MAIN_COLOR};
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  min-height: 40px;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
`;

const HeaderColumn = styled.td`
  display: flex;
  flex-direction: row;
  height: 25px;
  width: ${(props) => props.width || "100%"};
  text-align: left;
  margin: 0px 4px;
  align-items: center;
  gap: 5px;
  cursor: ${(props) => props.sortBy && "pointer"};
  justify-content: ${(props) =>
    props.centered ? "center" : props.menu ? "right" : "left"};
`;

const Footer = styled.tr`
  display: flex;
  flex-direction: row;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: ${theme.HEADER_TEXT};
  padding: 8px;
  border-bottom: 2px solid ${theme.BLACK};
  background: ${theme.MAIN_COLOR};
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  min-height: 40px;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  justify-content: space-between;
`;

const Page = styled.div`
  color: ${theme.HEADER_TEXT};
  padding: 1px;
  font-size: 16px;
  cursor: pointer;
  color: ${(props) => props.disabled && theme.BUTTON_COLOR_GREY};
  pointer-events: ${(props) => props.disabled && "none"};
`;

const ArrowUp = styled(Up)`
  color: ${(props) =>
    props.active ? theme.MAIN_COLOR : theme.BUTTON_COLOR_GREY};
  width: 10px;
`;

const ArrowDown = styled(Down)`
  color: ${(props) =>
    props.active ? theme.MAIN_COLOR : theme.BUTTON_COLOR_GREY};
  width: 10px;
`;

const PageSelection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const Total = styled.div``;

const Table = ({
  pagination,
  onPage,
  data,
  headers,
  onClick,
  onLimit,
  loading,
  sortBy,
  sortDirection,
  onSort,
  minHeight,
  minWidth,
  menu,
  rowHighlight,
  rowType = RowType.default,
}) => {
  const [quickMenuOpen, setQuickMenuOpen] = React.useState(null);
  const [menuOpen, setMenuOpen] = React.useState(null);

  const onMouseEnterRow = (indexValue) => {
    setQuickMenuOpen(indexValue);
  };

  const onMouseLeaveRow = (indexValue) => {
    setQuickMenuOpen(indexValue);
  };

  const onQuickMenuClick = (newMenuOpenValue) => {
    setMenuOpen(newMenuOpenValue);
  };

  const getRowComponent = (type) => {
    switch (type) {
      case "notification":
        return NotificationRow;
      default: {
        return DefaultRow;
      }
    }
  };
  return (
    <Main
      isLoading={loading}
      minHeight={minHeight}
      minWidth={minWidth}
      onClick={(e) => {
        loading && e.stopPropagation();
      }}
    >
      <thead>
        <Header>
          {headers &&
            headers.map((header, index) => (
              <HeaderColumn
                onClick={() =>
                  header.sortBy &&
                  onSort(header.sortBy, sortDirection === 1 ? -1 : 1)
                }
                centered={header.centered}
                width={header.width}
                sortBy={header.sortBy}
                key={index}
              >
                {header.sortBy && sortBy === header.sortBy ? (
                  sortDirection === -1 ? (
                    <ArrowUp
                      fill={
                        sortBy === header.sortBy && sortDirection === -1
                          ? theme.TABLE_ARROW_ACTIVE
                          : theme.TABLE_ARROW_INACTIVE
                      }
                    />
                  ) : (
                    <ArrowDown
                      fill={
                        sortBy === header.sortBy && sortDirection === 1
                          ? theme.TABLE_ARROW_ACTIVE
                          : theme.TABLE_ARROW_INACTIVE
                      }
                    />
                  )
                ) : null}
                {header.label}
              </HeaderColumn>
            ))}
          {menu && <HeaderColumn menu={true} width="100px"></HeaderColumn>}
        </Header>
      </thead>
      <tbody>
        {data?.map((data, index) => {
          const quickMenuOption = menu
            ? menu.find((item) => !item.disabled(data))
            : null;
          const rowComponent = getRowComponent(rowType);
          return rowComponent({
            data,
            index,
            headers,
            onClick,
            onLimit,
            menu,
            rowHighlight,
            quickMenuOpen,
            onQuickMenuClick,
            menuOpen,
            onMouseEnterRow,
            onMouseLeaveRow,
            quickMenuOption,
          });
        })}
        <tr>
          {loading && (
            <td>
              <Loader
                style={{ margin: "auto" }}
                inline={data.length === 0}
                size="huge"
                active
              />
            </td>
          )}
        </tr>
      </tbody>
      <tfoot>
        {onPage && onLimit && (
          <Footer>
            <td>
              <Selector
                width="100px"
                onSelect={onLimit}
                options={[
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                  { label: "200", value: 200 },
                  { label: "All", value: 10000 },
                ]}
              />
            </td>
            <td>
              <PageSelection>
                <Page
                  disabled={pagination.page === 1}
                  onClick={() => onPage(pagination.page - 1)}
                >
                  {"<Prev"}
                </Page>
                <Page
                  disabled={
                    pagination.page ===
                    Math.ceil(pagination.total / pagination.limit)
                  }
                  onClick={() => onPage(pagination.page + 1)}
                >
                  {"Next>"}
                </Page>
              </PageSelection>
            </td>
            <td>
              <Total>Total: {pagination.total}</Total>
            </td>
          </Footer>
        )}
      </tfoot>
    </Main>
  );
};
export default Table;
